import { useEffect, useRef, useState } from 'react';
import { getClickAccessibilityProps } from '../../utils/accessibilityUtils';
import { PopoverProps } from './Popover.types';
import { getPlacement, handleInteraction } from './Popover.utils';

const usePresenter = (props: PopoverProps) => {
  const {
    target,
    action = 'Click',
    content,
    offset,
    onHide,
    onOpen,
    alignment,
    direction,
    hideOnClickAway = true,
    show: defaultShow = false,
    contentProps,
    disabled,
    keepContentOnItemClick = false,
    flip,
    className,
    container,
    overlayClassName,
    contentPosition = 'absolute',
    ...otherProps
  } = props;

  // Refs for the popover container and target elements
  const containerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  // State to manage the visibility of the popover
  const [show, setShow] = useState<boolean>(defaultShow);

  // ref to store the initial transform when position is fixed for content
  const initialTransformRef = useRef<string | undefined>();

  // Cleanup effect to ensure onHide callback is called when the popover is unmounted
  useEffect(() => {
    if (show && onOpen) {
      onOpen();
    }

    return () => {
      // Notify onHide callback if the popover is visible upon unmounting
      if (show && onHide) {
        onHide();
      }
    };
  }, [show, onHide, onOpen]);

  // Set initial show state based on defaultShow prop if action is not provided
  useEffect(() => {
    if (defaultShow !== undefined && !action) {
      setShow(defaultShow);
    }
  }, [defaultShow, action]);

  // Calculate placement using utility function
  const placement = getPlacement(alignment, direction);

  // Handle hide event 
  const handleOnHide = () => {
    setShow(false);
    if (onHide) {
      onHide();
    }
  };

  const onClickListener = action === 'Click' ? () => !keepContentOnItemClick && handleInteraction(action, show, setShow, 'Click', undefined, disabled) : undefined;
  const onDoubleClickListener = action === 'DoubleClick' ? () => !keepContentOnItemClick && handleInteraction(action, show, setShow, 'DoubleClick', undefined, disabled) : undefined;

  const onMouseOutListener = action === 'Hover' ? () => handleInteraction(action, show, setShow, 'Hover', false, disabled) : undefined;
  const onMouseOverListener = action === 'Hover' ? () => handleInteraction(action, show, setShow, 'Hover', true, disabled) : undefined;

  const popoverAccessibilityProps = !keepContentOnItemClick ? getClickAccessibilityProps({
    actions: {
      onClick: action === 'Click' ? onClickListener : onDoubleClickListener,
    },
    addIndex: !keepContentOnItemClick,
  }) : {};

  const targetOnClickListener = () => keepContentOnItemClick && handleInteraction(action, show, setShow, 'Click', undefined, disabled);
  const targetOnDoubleClickListener = () => keepContentOnItemClick && handleInteraction(action, show, setShow, 'DoubleClick', undefined, disabled);

  const targetAccessibilityProps = keepContentOnItemClick ? getClickAccessibilityProps({
    actions: {
      onClick: action === 'Click' ? targetOnClickListener : targetOnDoubleClickListener,
    },
    addIndex: keepContentOnItemClick,
  }) : {};

  const openPopover = () => {
    setShow(true);
  };

  return {
    show,
    offset,
    placement,
    handleOnHide,
    openPopover,
    containerRef,
    targetRef,
    initialTransformRef,
    action,
    hideOnClickAway,
    defaultShow,
    keepContentOnItemClick,
    contentPosition,
    onClickListener,
    onDoubleClickListener,
    onMouseOutListener,
    onMouseOverListener,
    targetOnClickListener,
    targetOnDoubleClickListener,
    popoverAccessibilityProps,
    targetAccessibilityProps,
    otherProps,
  };
}
export default usePresenter;